import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send cookies with requests
});

// Properties
export const getProperties = () => api.get('/properties');
export const getProperty = (id) => api.get(`/properties/${id}`);
export const createProperty = (data) => api.post('/properties', data);
export const updateProperty = (id, data) => api.put(`/properties/${id}`, data);
export const deleteProperty = (id) => api.delete(`/properties/${id}`);

// Property files
export const listPropertyFiles = (propertyId) =>
  api.get(`/properties/${propertyId}/files`);
export const listAllOrgFiles = () => api.get('/files/all');
export const uploadPropertyFile = (propertyId, file, category, description) => {
  const form = new FormData();
  form.append('file', file);
  form.append('category', category || 'other');
  form.append('description', description || '');
  return api.post(`/properties/${propertyId}/files`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const deletePropertyFile = (propertyId, fileId) =>
  api.delete(`/properties/${propertyId}/files/${fileId}`);
export const downloadPropertyFileUrl = (propertyId, fileId) =>
  `${API}/properties/${propertyId}/files/${fileId}/download`;

// AI Chat
export const chatSendMessage = (message, session_id) =>
  api.post('/chat/message', { message, session_id });
export const chatGetOrCreateSession = () => api.get('/chat/session');
export const chatListMessages = (session_id) =>
  api.get('/chat/messages', { params: { session_id } });

// Guests
export const getGuests = () => api.get('/guests');

// Org users (managers/owners only) — used for task collaboration pickers.
export const listOrgUsers = () => api.get('/users');

export const replyToNotification = (id, text) =>
  api.post(`/notifications/${id}/reply`, { text });

// My Day Digest
export const previewMyDigest = () => api.get('/digests/preview');
export const sendMyDigest = () => api.post('/digests/send-mine');
export const updateDigestPrefs = (patch) => api.put('/digests/prefs', patch);
export const getGuest = (id) => api.get(`/guests/${id}`);
export const createGuest = (data) => api.post('/guests', data);
export const deleteGuest = (id) => api.delete(`/guests/${id}`);

// Bookings
export const getBookings = (propertyId = null) => {
  const params = propertyId ? { property_id: propertyId } : {};
  return api.get('/bookings', { params });
};
export const getBooking = (id) => api.get(`/bookings/${id}`);
export const createBooking = (data) => api.post('/bookings', data);
export const updateBooking = (id, data) => api.put(`/bookings/${id}`, data);
export const deleteBooking = (id) => api.delete(`/bookings/${id}`);
export const checkBookingConflicts = (data) => api.post('/bookings/check-conflicts', data);

// Cleaning Tasks
export const getCleaningTasks = (propertyId = null) => {
  const params = propertyId ? { property_id: propertyId } : {};
  return api.get('/cleaning-tasks', { params });
};
export const createCleaningTask = (data) => api.post('/cleaning-tasks', data);
export const updateCleaningTask = (id, data) => api.put(`/cleaning-tasks/${id}`, data);
export const updateCleaningTaskStatus = (id, status) => 
  api.put(`/cleaning-tasks/${id}/status?status=${status}`);
export const updateCleaningTaskPhotos = (id, photos) => 
  api.put(`/cleaning-tasks/${id}/photos`, { photos });
export const addCleaningWorkNote = (id, note, hours = 0) => 
  api.post(`/cleaning-tasks/${id}/work-notes`, { note, hours });
export const deleteCleaningWorkNote = (taskId, noteId) => 
  api.delete(`/cleaning-tasks/${taskId}/work-notes/${noteId}`);
export const deleteCleaningTask = (id) => api.delete(`/cleaning-tasks/${id}`);
export const rescheduleCleaningTask = (id, scheduledDate) => 
  api.put(`/cleaning-tasks/${id}/reschedule`, { scheduled_date: scheduledDate });

// Maintenance Tasks
export const getMaintenanceTasks = (propertyId = null) => {
  const params = propertyId ? { property_id: propertyId } : {};
  return api.get('/maintenance-tasks', { params });
};
export const createMaintenanceTask = (data) => api.post('/maintenance-tasks', data);
export const updateMaintenanceTask = (id, data) => api.put(`/maintenance-tasks/${id}`, data);
export const updateMaintenanceTaskStatus = (id, status) => 
  api.put(`/maintenance-tasks/${id}/status?status=${status}`);
export const updateMaintenanceTaskPhotos = (id, photos) => 
  api.put(`/maintenance-tasks/${id}/photos`, { photos });
export const addMaintenanceWorkNote = (id, note, hours = 0) => 
  api.post(`/maintenance-tasks/${id}/work-notes`, { note, hours });
export const deleteMaintenanceWorkNote = (taskId, noteId) => 
  api.delete(`/maintenance-tasks/${taskId}/work-notes/${noteId}`);
export const deleteMaintenanceTask = (id) => api.delete(`/maintenance-tasks/${id}`);
export const rescheduleMaintenanceTask = (id, scheduledDate) => 
  api.put(`/maintenance-tasks/${id}/reschedule`, { scheduled_date: scheduledDate });

// Expenses
export const getExpenses = (propertyId = null, category = null) => {
  const params = {};
  if (propertyId) params.property_id = propertyId;
  if (category) params.category = category;
  return api.get('/expenses', { params });
};
export const getExpense = (id) => api.get(`/expenses/${id}`);
export const createExpense = (data) => api.post('/expenses', data);
export const updateExpense = (id, data) => api.put(`/expenses/${id}`, data);
export const updateExpenseReceipts = (id, photos) => 
  api.put(`/expenses/${id}/receipts`, { photos });
export const deleteExpense = (id) => api.delete(`/expenses/${id}`);
export const getExpensesByCategory = (propertyId = null) => {
  const params = propertyId ? { property_id: propertyId } : {};
  return api.get('/expenses/summary/by-category', { params });
};
export const getExpensesByMonth = (propertyId = null, year = null) => {
  const params = {};
  if (propertyId) params.property_id = propertyId;
  if (year) params.year = year;
  return api.get('/expenses/summary/by-month', { params });
};

// Calendar Events
export const getCalendarEvents = (propertyId = null) => {
  const params = propertyId ? { property_id: propertyId } : {};
  return api.get('/calendar/events', { params });
};

// Dashboard Stats
export const getDashboardStats = (propertyId = null) => {
  const params = propertyId ? { property_id: propertyId } : {};
  return api.get('/dashboard/stats', { params });
};

// Workers
export const getWorkers = (isActive = null, skill = null) => {
  const params = {};
  if (isActive !== null) params.is_active = isActive;
  if (skill) params.skill = skill;
  return api.get('/workers', { params });
};
export const getWorker = (id) => api.get(`/workers/${id}`);
export const createWorker = (data) => api.post('/workers', data);
export const updateWorker = (id, data) => api.put(`/workers/${id}`, data);
export const deleteWorker = (id) => api.delete(`/workers/${id}`);
export const getWorkerSchedule = (workerId, startDate = null, endDate = null) => {
  const params = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  return api.get(`/workers/${workerId}/schedule`, { params });
};

// Task Assignment
export const assignCleaningTask = (taskId, workerId) => 
  api.put(`/cleaning-tasks/${taskId}/assign?worker_id=${workerId}`);
export const assignMaintenanceTask = (taskId, workerId) => 
  api.put(`/maintenance-tasks/${taskId}/assign?worker_id=${workerId}`);

// Reports
export const getLaborHoursReport = (propertyId = null, workerId = null, startDate = null, endDate = null) => {
  const params = {};
  if (propertyId) params.property_id = propertyId;
  if (workerId) params.worker_id = workerId;
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  return api.get('/reports/labor-hours', { params });
};
export const exportLaborHoursCSV = (propertyId = null, workerId = null, startDate = null, endDate = null) => {
  const params = new URLSearchParams();
  if (propertyId) params.append('property_id', propertyId);
  if (workerId) params.append('worker_id', workerId);
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  return `${API}/reports/labor-hours/export?${params.toString()}`;
};

// Invoices
export const getInvoices = (propertyId = null, status = null) => {
  const params = {};
  if (propertyId) params.property_id = propertyId;
  if (status) params.status = status;
  return api.get('/invoices', { params });
};
export const getInvoice = (id) => api.get(`/invoices/${id}`);
export const createInvoice = (data) => api.post('/invoices', data);
export const updateInvoiceStatus = (id, status, paidDate = null) => 
  api.put(`/invoices/${id}/status`, { status, paid_date: paidDate });
export const deleteInvoice = (id) => api.delete(`/invoices/${id}`);

// Recurring Invoices
export const getRecurringInvoices = () => api.get('/recurring-invoices');
export const createRecurringInvoice = (data) => api.post('/recurring-invoices', data);
export const updateRecurringInvoice = (id, data) => api.put(`/recurring-invoices/${id}`, data);
export const deleteRecurringInvoice = (id) => api.delete(`/recurring-invoices/${id}`);
export const runRecurringInvoiceNow = (id) => api.post(`/recurring-invoices/${id}/run-now`);

// Notifications
export const getNotifications = (unreadOnly = false) =>
  api.get('/notifications', { params: unreadOnly ? { unread_only: true } : {} });
export const getUnreadCount = () => api.get('/notifications/unread-count');
export const markNotificationRead = (id) => api.put(`/notifications/${id}/read`);
export const markAllNotificationsRead = () => api.put('/notifications/read-all');
export const deleteNotification = (id) => api.delete(`/notifications/${id}`);

// Milestone Templates
export const getMilestoneTemplates = () => api.get('/milestone-templates');
export const createMilestoneTemplate = (data) => api.post('/milestone-templates', data);
export const updateMilestoneTemplate = (id, data) => api.put(`/milestone-templates/${id}`, data);
export const deleteMilestoneTemplate = (id) => api.delete(`/milestone-templates/${id}`);
export const applyTemplateToProject = (projectId, templateId, mode = 'append') =>
  api.post(`/renovation-projects/${projectId}/apply-template`, { template_id: templateId, mode });
export const saveProjectAsTemplate = (projectId, name, description = '') =>
  api.post(`/renovation-projects/${projectId}/save-as-template`, { name, description });

// Work Orders
export const getWorkOrders = (params = {}) => api.get('/work-orders', { params });
export const getWorkOrder = (id) => api.get(`/work-orders/${id}`);
export const createWorkOrder = (data) => api.post('/work-orders', data);
export const updateWorkOrder = (id, data) => api.put(`/work-orders/${id}`, data);
export const deleteWorkOrder = (id) => api.delete(`/work-orders/${id}`);
export const convertWorkOrderToInvoice = (id) => api.post(`/work-orders/${id}/convert-to-invoice`);

// iCal Sync
export const getPropertyICalUrl = (propertyId) => api.get(`/properties/${propertyId}/ical-url`);
export const getPropertyICalFeed = (propertyId) => `${API}/properties/${propertyId}/ical`;

// iCal Feeds (Import)
export const getICalFeeds = (propertyId = null) => {
  const params = propertyId ? { property_id: propertyId } : {};
  return api.get('/ical-feeds', { params });
};
export const createICalFeed = (data) => api.post('/ical-feeds', data);
export const updateICalFeed = (id, data) => api.put(`/ical-feeds/${id}`, data);
export const deleteICalFeed = (id) => api.delete(`/ical-feeds/${id}`);
export const syncICalFeed = (feedId) => api.post(`/ical-feeds/${feedId}/sync`);
export const syncAllFeeds = (propertyId) => api.post(`/properties/${propertyId}/sync-all`);

// Analytics
export const getRevenueAnalytics = (propertyId = null, year = null) => {
  const params = {};
  if (propertyId) params.property_id = propertyId;
  if (year) params.year = year;
  return api.get('/analytics/revenue', { params });
};
export const getOccupancyAnalytics = (propertyId = null, year = null) => {
  const params = {};
  if (propertyId) params.property_id = propertyId;
  if (year) params.year = year;
  return api.get('/analytics/occupancy', { params });
};
export const getProfitAnalytics = (propertyId = null, year = null) => {
  const params = {};
  if (propertyId) params.property_id = propertyId;
  if (year) params.year = year;
  return api.get('/analytics/profit', { params });
};
export const getBookingSourcesAnalytics = (propertyId = null, year = null) => {
  const params = {};
  if (propertyId) params.property_id = propertyId;
  if (year) params.year = year;
  return api.get('/analytics/booking-sources', { params });
};
export const getAnalyticsSummary = (propertyId = null, year = null) => {
  const params = {};
  if (propertyId) params.property_id = propertyId;
  if (year) params.year = year;
  return api.get('/analytics/summary', { params });
};

// Recurring Maintenance Tasks
export const setTaskRecurring = (taskId, isRecurring, pattern = null) =>
  api.put(`/maintenance-tasks/${taskId}/set-recurring`, { 
    is_recurring: isRecurring, 
    recurrence_pattern: pattern 
  });
export const getRecurringTasks = (propertyId = null) => {
  const params = propertyId ? { property_id: propertyId } : {};
  return api.get('/maintenance-tasks/recurring', { params });
};

// Password Reset
export const forgotPassword = (email) => api.post('/auth/forgot-password', { email });
export const resetPassword = (token, newPassword) => 
  api.post('/auth/reset-password', { token, new_password: newPassword });
export const verifyResetToken = (token) => api.get(`/auth/verify-reset-token?token=${token}`);

// Email Verification
export const verifyEmail = (token) => api.post('/auth/verify-email', { token });
export const resendVerification = () => api.post('/auth/resend-verification');

// Invite Codes (Manager-only)
export const createInviteCode = (role, expiresInDays = 14) =>
  api.post('/auth/invite-codes', { role, expires_in_days: expiresInDays });
export const listInviteCodes = () => api.get('/auth/invite-codes');
export const revokeInviteCode = (code) => api.delete(`/auth/invite-codes/${code}`);

// Waitlist (paid plans coming soon)
export const joinWaitlist = (data) => api.post('/waitlist', data);
export const getWaitlistCount = () => api.get('/waitlist/count');

export default api;
